import { useEffect } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';

import { BigBuyBarDesktopNewSection } from 'productTemplate/components/buyBar/BigBuyBarDesktopNewSection';
import { BigBuyBarDesktopTabButton } from 'productTemplate/components/buyBar/BigBuyBarDesktopTabButton';
import { BigBuyBarView } from 'store/types/pdpStateTypes';
import { BUY_BAR_TABS_STYLE } from 'productTemplate/enums/desktopBuyBar';
import { HiddenScrollDiv } from 'shared/styles/HiddenScrollDiv';
import { IProductTemplate } from 'shared/types/ProductTemplate';
import { OffersSection } from 'productTemplate/components/buyBar/OffersSection';
import { sendBuyBarTrackingEvent } from 'productTemplate/utils/sendBuyBarTrackingEvent';
import { setBigBuyBarView } from 'store/slice/pdpStateSlice';
import { t } from 'localization/utils/t';
import { TABS } from 'productTemplate/enums/buyBarMobile';
import { useBigBuyBarDesktopView } from 'productTemplate/hooks/useBigBuyBarDesktopView';
import { UsedSection } from 'productTemplate/components/buyBar/UsedSection';
import { useOffersContext } from 'productTemplate/hooks/useOffersContext';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { variableFontRegular } from 'styles/theme/text';
import { VARIANT_KEYS } from 'productTemplate/enums/variantKeys';
import { ViewSelector } from 'productTemplate/components/ViewSelector';

const TP = 'productTemplate.components.buyBar.BigBuyBarDesktopTabs';

interface IBigBuyBarDesktopTabsProps {
  isBigBuyBarViewExpanded: boolean;
}

export const BigBuyBarDesktopTabs = ({ isBigBuyBarViewExpanded }: IBigBuyBarDesktopTabsProps) => {
  const { query } = useRouter();
  const { bigBuyBarView, size } = useStoreSelector((state) => state.pdpState);
  const storeDispatch = useStoreDispatch();
  const { productTemplate, buyBarVariants } = useStoreSelector((state) => state.pdp);
  const { mapSizeToVariants } = buyBarVariants;
  const { slug, localizedSpecialDisplayPriceCents, sku }: IProductTemplate = productTemplate;

  const { isOfferable } = useOffersContext();
  const variants = mapSizeToVariants?.[size];

  const hasUsedVariantsForSize = Boolean(variants?.[VARIANT_KEYS.USED]);
  const { isBuyBarSizeLarge, setBuyBarSizeLarge } = useBigBuyBarDesktopView();

  const handleVariantsChange = (products) => {
    // As of today, the grid has 4 products max per row, so...
    const hasTwoOrMoreRows = products?.length > 4;
    setBuyBarSizeLarge(hasTwoOrMoreRows);
  };

  const { fetchTopOffers } = useOffersContext();

  useEffect(() => {
    if (bigBuyBarView === 'used' && query?.tab !== 'used' && !hasUsedVariantsForSize) {
      // eslint-disable-next-line no-console
      storeDispatch(setBigBuyBarView('new'));
    }

    if (bigBuyBarView === 'offer' && !isOfferable) {
      storeDispatch(setBigBuyBarView('new'));
    }
  }, [bigBuyBarView, hasUsedVariantsForSize, isOfferable]);

  useEffect(() => {
    if (query?.tab) {
      const tab = (query?.tab === TABS.USED ? TABS.USED : TABS.NEW) as BigBuyBarView;
      storeDispatch(setBigBuyBarView(tab));
    }
  }, []);

  useEffect(() => {
    // When moving to New or Offer tab, reset BuyBar height to default
    if (bigBuyBarView === 'new' || bigBuyBarView === 'offer') {
      setBuyBarSizeLarge(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bigBuyBarView]);

  useEffect(() => {
    if (isBigBuyBarViewExpanded) {
      sendBuyBarTrackingEvent({
        eventName: 'PDP_BUY_BAR_VIEW',
        productVariants: variants ? Object?.values(variants) : [],
        retailPrice: localizedSpecialDisplayPriceCents,
        sku,
        slug,
        section: bigBuyBarView,
        size,
      });
    }
  }, [
    size,
    bigBuyBarView,
    isBigBuyBarViewExpanded,
    variants,
    localizedSpecialDisplayPriceCents,
    slug,
    sku,
  ]);

  const handleMakeOfferClick = async () => {
    await fetchTopOffers();
  };

  return (
    <BigBuyBarDesktopTabs.Wrapper $isBuyBarSizeLarge={isBuyBarSizeLarge}>
      <BigBuyBarDesktopTabs.DisappearingBorder />
      <BigBuyBarDesktopTabs.Scrollable $isBuyBarSizeLarge={isBuyBarSizeLarge}>
        <BigBuyBarDesktopTabs.Header>
          <BigBuyBarDesktopTabs.Buttons>
            <BigBuyBarDesktopTabButton
              tab="new"
              title={t(`${TP}.buyNew`, 'Buy New')}
              data-qa="new_condition"
            />
            {hasUsedVariantsForSize && (
              <BigBuyBarDesktopTabButton
                tab="used"
                title={t(`${TP}.buyUsed`, 'Buy Used')}
                data-qa="used_condition"
              />
            )}
            {isOfferable && (
              <BigBuyBarDesktopTabButton
                tab="offer"
                title={t(`${TP}.makeOffer`, 'Make Offer')}
                data-qa="make_offer"
                onClick={handleMakeOfferClick}
              />
            )}
          </BigBuyBarDesktopTabs.Buttons>
        </BigBuyBarDesktopTabs.Header>
        <BigBuyBarDesktopTabs.PanelView>
          <ViewSelector
            selectedView={bigBuyBarView}
            new={<BigBuyBarDesktopNewSection size={size} />}
            used={<UsedSection selectedSize={size} onVariantsChange={handleVariantsChange} />}
            offer={<OffersSection selectedSize={size} />}
          />
        </BigBuyBarDesktopTabs.PanelView>
      </BigBuyBarDesktopTabs.Scrollable>
    </BigBuyBarDesktopTabs.Wrapper>
  );
};

BigBuyBarDesktopTabs.Wrapper = styled(HiddenScrollDiv)<{ $isBuyBarSizeLarge: boolean }>`
  height: ${({ $isBuyBarSizeLarge }) =>
    $isBuyBarSizeLarge
      ? BUY_BAR_TABS_STYLE.CONTENT_LARGE_HEIGHT
      : BUY_BAR_TABS_STYLE.CONTENT_DEFAULT_HEIGHT}px;
  overflow-y: scroll;
  position: relative;
`;

BigBuyBarDesktopTabs.Scrollable = styled(HiddenScrollDiv)<{ $isBuyBarSizeLarge: boolean }>`
  height: 100%;
  padding-bottom: 40px;
  overflow-y: scroll;
`;

// Helps create the "effect" of a border bottom appeaing when header with buttons becomes sticky;
BigBuyBarDesktopTabs.DisappearingBorder = styled.div`
  position: absolute;
  top: 0;
  border-bottom: 1px solid #000;
  height: ${BUY_BAR_TABS_STYLE.TABS_STICKY_HEIGHT + 1}px;
  width: 100%;
`;

BigBuyBarDesktopTabs.Header = styled.div`
  width: 100%;
  position: sticky;
  top: ${BUY_BAR_TABS_STYLE.TABS_STICKY_HEIGHT - BUY_BAR_TABS_STYLE.TABS_DEFAULT_HEIGHT}px;
  background-color: #fff;
`;

// TODO: Need to be more responsive with min/max - width
// Having fixed width for stretching and expanding elements, fixed positioned and flex elements is causing
// most of display inaccuracies
BigBuyBarDesktopTabs.Buttons = styled.div`
  width: 504px;
  height: ${BUY_BAR_TABS_STYLE.TABS_DEFAULT_HEIGHT}px;

  box-sizing: border-box;
  margin: 0 auto;
  padding-top: 32px;
  padding-bottom: 16px;

  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: center;

  ${variableFontRegular({ size: '12px' })}
`;

BigBuyBarDesktopTabs.PanelView = styled.div`
  width: 504px;
  margin: -2px auto 0 auto;
`;
